import styled from 'styled-components'

import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebouncedCallback } from 'hooks'

import { Button, Input, Option, Select } from './'

const Locations = styled.div``

const Wrap = styled.div``

const useAutocomplete = ({ debounce = 300 } = {}) => {
  const placesLib = useMapsLibrary('places')
  const [ready, setReady] = useState(false)
  const [value, setValue] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const sessionToken = useRef(null)

  useEffect(() => {
    if (!placesLib) return
    sessionToken.current = new placesLib.AutocompleteSessionToken()
    setReady(true)
  }, [placesLib])

  const fetchSuggestions = useCallback(async (input) => {
    if (!input || !placesLib) {
      setSuggestions([])
      return
    }
    const { suggestions: results } =
      await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: sessionToken.current
      })
    setSuggestions(results.map((s) => s.placePrediction))
  }, [placesLib])

  const debouncedFetch = useDebouncedCallback(fetchSuggestions, debounce)

  const handleChange = (input) => {
    setValue(input)
    debouncedFetch(input)
  }

  const clear = () => {
    setSuggestions([])
    sessionToken.current = new placesLib.AutocompleteSessionToken()
  }

  return { ready, value, setValue, suggestions, handleChange, clear }
}

export const LocationTypeAhead = ({
  onSelect = () => true,
  initialValue = ''
}) => {
  const { ready, value, setValue, suggestions, handleChange, clear } =
    useAutocomplete()
  const [index, setIndex] = useState(-1)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const handleKeyboard = (e) => {
      switch (e.keyCode) {
        case 40:
          if (index < suggestions.length - 1) setIndex(index + 1)
          break
        case 38:
          if (index > 0) setIndex(index - 1)
          break
        case 13:
          if (suggestions[index]) handleSelect(suggestions[index])
          setIndex(-1)
          break
        default:
      }
    }
    window.addEventListener('keydown', handleKeyboard, { passive: true })
    return () => window.removeEventListener('keydown', handleKeyboard, { passive: true })
  }, [suggestions, index])

  const handleSelect = async (prediction) => {
    const place = prediction.toPlace()
    await place.fetchFields({
      fields: ['displayName', 'formattedAddress', 'addressComponents', 'location', 'viewport', 'types']
    })

    const address_components = (place.addressComponents || []).map((c) => ({
      long_name: c.longText,
      short_name: c.shortText,
      types: c.types
    }))

    const result = {
      description: prediction.text?.text ?? '',
      place_id: prediction.placeId,
      name: place.displayName,
      formatted_address: place.formattedAddress,
      address_components,
      types: place.types ?? [],
      geometry: {
        location: place.location,
        viewport: place.viewport
      }
    }

    onSelect(result)
    setValue(place.displayName ?? prediction.text?.text ?? '')
    clear()
  }

  return (
    <Locations>
      <Select $show={ready} className="autocomplete-dropdown-container">
        <Input
          value={value}
          onChange={({ target: { value } }) => handleChange(value)}
          disabled={!ready}
        />
        {suggestions.map((prediction, i) => (
          <Option
            key={prediction.placeId}
            onClick={() => handleSelect(prediction)}
            className={index === i ? 'active' : null}
          >
            {prediction.text?.text}
          </Option>
        ))}
        {suggestions.length > 0 && (
          <Option>
            <img
              src="https://developers.google.com/maps/documentation/images/powered_by_google_on_white.png"
              alt="Powered by Google"
            />
          </Option>
        )}
      </Select>
    </Locations>
  )
}

const LocationSearchInner = ({ onSelect = () => true }) => {
  const placesLibrary = useMapsLibrary('places')

  const [address, setAddress] = useState({
    coords: { lat: 41.8853195, lng: -87.6285088 }
  })

  const handleSelect = (result) => {
    setAddress(result)
  }

  useEffect(() => {
    if (!address.geometry) return
    onSelect(address)
  }, [address])

  if (!placesLibrary) return null

  return (
    <Wrap>
      <LocationTypeAhead onSelect={handleSelect} />
    </Wrap>
  )
}

export const LocationSearch = ({ onSelect = () => true }) => (
  <APIProvider apiKey={process.env.GOOGLE_API_KEY} libraries={['places']}>
    <LocationSearchInner onSelect={onSelect} />
  </APIProvider>
)
